import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { Icon } from './Icon';

export interface OverflowMenuAction {
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
}

export interface OverflowMenuProps {
  readonly label: string;
  readonly actions: readonly OverflowMenuAction[];
  readonly disabled?: boolean;
}

/**
 * Divulgation légère d'actions secondaires (1 à 3 boutons) — pas un widget ARIA `menu` complet,
 * qui exigerait une navigation flèches/roving-tabindex superflue pour si peu d'actions. Ferme
 * au clic extérieur et à Échap. Rend `null` sans action : jamais un déclencheur vide.
 */
export function OverflowMenu({ label, actions, disabled = false }: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const popover: CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 6,
    display: 'grid',
    minWidth: 180,
    padding: 6,
    background: 'var(--surface-raised)',
    border: '1px solid var(--accent-line)',
    borderRadius: 'var(--r-md)',
    boxShadow: 'var(--shadow-lift)',
    zIndex: 3,
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', flex: '0 0 auto' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        className="ai-iconbtn ai-focusable"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        style={{
          width: 34,
          height: 34,
          display: 'inline-grid',
          placeItems: 'center',
          borderRadius: 'var(--r-pill)',
          border: '1px solid rgba(255,255,255,.08)',
          background: 'transparent',
          color: 'var(--text-muted)',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.38 : 1,
        }}
      >
        <Icon name="menu" size={16} />
      </button>
      {open ? (
        <div role="presentation" style={popover}>
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className="ai-focusable"
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                action.onSelect();
              }}
              style={{
                textAlign: 'left',
                padding: '10px 12px',
                borderRadius: 'var(--r-sm)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                fontSize: 13,
                fontWeight: 300,
                color: action.danger ? '#B0453F' : 'var(--text-body)',
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
